import {Injectable} from '@angular/core';
import {TableColumn} from './model/table-column';
import {TableColumnLayout} from './model/table-column-layout';
import {AuthStorage} from '../../auth/auth-storage';

export interface ResolvedTableColumns<T extends TableColumn> {
  columns: T[];
  selected: T[];
  selectedMap: Map<string, T>;
}

@Injectable()
export class TableColumnsService {

  private static readonly MIN_WIDTH = 32;
  private static readonly MAX_WIDTH = 200;

  public resolve<T extends TableColumn>(tableKey: string,
                                        allColumns: T[],
                                        defaultLayout: TableColumnLayout[]): ResolvedTableColumns<T> {
    let stored = AuthStorage.getTableColumns(tableKey);
    return this.applyLayout(allColumns, stored != null ? stored : defaultLayout);
  }

  public applyLayout<T extends TableColumn>(allColumns: T[], layout: TableColumnLayout[]): ResolvedTableColumns<T> {
    let columns = allColumns.map(column => Object.assign({}, column));
    let selected: T[] = [];
    for (let entry of layout) {
      let column = columns.find(it => it.name === entry.name);
      if (column != null && selected.indexOf(column) === -1) {
        this.applyEntry(column, entry);
        selected.push(column);
      }
    }
    for (let column of columns) {
      if (column.required && selected.indexOf(column) === -1) {
        selected.push(column);
      }
    }
    return {columns: columns, selected: selected, selectedMap: this.toMap(selected)};
  }

  public filterControls(columns: TableColumn[], values: { [name: string]: any } = {}): { [name: string]: any } {
    let controls: { [name: string]: any } = {};
    for (let column of columns) {
      if (column.filter != null) {
        controls[column.name] = values[column.name];
      }
    }
    return controls;
  }

  public toggleSelected<T extends TableColumn>(selected: T[], column: T): T[] {
    let result = selected;
    if (!column.required) {
      result = selected.some(it => it.name === column.name)
        ? selected.filter(it => it.name !== column.name)
        : selected.concat([column]);
    }
    return result;
  }

  public reorder<T extends TableColumn>(selected: T[],
                                        movedIdx: number,
                                        previousIndex: number,
                                        currentIndex: number): T[] {
    let reordered = selected.slice();
    let moved = reordered.find(it => it.idx === movedIdx);
    reordered.splice(previousIndex, 1);
    reordered.splice(currentIndex, 0, moved);
    return reordered;
  }

  public clampWidth(width: number): number {
    let clamped = width <= TableColumnsService.MIN_WIDTH
      ? TableColumnsService.MIN_WIDTH
      : Math.round(width);
    return clamped > TableColumnsService.MAX_WIDTH ? TableColumnsService.MAX_WIDTH : clamped;
  }

  public clearFiltering<T extends TableColumn>(selected: T[]): T[] {
    let cleared = selected.filter(it => it.filtering);
    cleared.forEach(column => column.filtering = false);
    return cleared;
  }

  public toMap<T extends TableColumn>(selected: T[]): Map<string, T> {
    let map = new Map<string, T>();
    for (let column of selected) {
      map.set(column.name, column);
    }
    return map;
  }

  public toLayout(selected: TableColumn[]): TableColumnLayout[] {
    return selected.map(it => ({name: it.name, size: it.size, filtering: it.filtering}));
  }

  public persist(tableKey: string, selected: TableColumn[]): void {
    AuthStorage.saveTableColumns(tableKey, this.toLayout(selected));
  }

  private applyEntry(column: TableColumn, entry: TableColumnLayout): void {
    if (entry.size != null) {
      column.size = entry.size;
    }
    if (entry.filtering != null) {
      column.filtering = entry.filtering;
    }
  }

}
